// packages/i18n/src/notifications-keys.ts
//
// Per CONTEXT.md (Wallet Notifications) + (Notification Content).
// Covers goal: G-NOTIF-02 (i18n catalog).
//
// vg-binding: CONTEXT:decisions=P9.3.D-09
// vg-binding: CONTEXT:decisions=P9.4.D-15
// vg-binding: CONTEXT:decisions=P4.11.D-08,P4.11.D-03
// vg-binding: WORKFLOW-SPECS:WF-001
// vg-binding: CONTEXT:decisions=P4.4.2.D-16
// vg-binding: CONTEXT:decisions=P14.1.D-04
// vg-binding: CONTEXT:decisions=P14.1.D-18
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Phase 4.12 Task 12 (P4.12.D-33 / G-24 / TS-32) — the confirm-time MONEY
// notification. Its amount is rendered from the order's persisted
// `costSnapshot` (incl. tax + shipping), never from a live re-derivation, and
// it ships with `i18n_key` + `i18n_params` so the persist worker renders at
// each recipient's `User.language` (project i18n Tầng-1 rule).
// vg-binding: CONTEXT:decisions=P4.12.D-29,P4.12.D-32,P4.12.D-33
// vg-binding: P4.12.D-29
// vg-binding: P4.12.D-32
// vg-binding: P4.12.D-33
//
// Authoritative registry of notification event keys consumed by the dispatcher
// (apps/api/src/modules/notifications/dispatcher.service.ts — Task 15). The
// dispatcher MUST validate `event_type` against this set before persisting a
// Notification doc — fail-fast on unknown keys.
export const NOTIFICATION_KEYS = {
    // Merchant-facing
    WALLET_TOPUP_CONFIRMED: 'notification.wallet_topup_confirmed',
    INVOICE_GENERATED: 'notification.invoice_generated',
    WITHDRAW_APPROVED: 'notification.withdraw_approved',
    WALLET_LOW_BALANCE: 'notification.wallet_low_balance',
    WALLET_LOW_BALANCE_TEST: 'notification.wallet_low_balance_test',
    // Admin-facing
    NEW_TOPUP_REQUEST: 'notification.new_topup_request',
    NEW_WITHDRAW_REQUEST: 'notification.new_withdraw_request',
    PENDING_REQUEST_REMINDER: 'notification.pending_request_reminder',
    MERCHANT_CREDIT_OVERDUE: 'notification.merchant_credit_overdue',
    // Ops alerts
    IMAP_FAIL: 'notification.imap_fail',
    BULLMQ_DLQ_ALERT: 'notification.bullmq_dlq_alert',
    AUTO_RECHARGE_FAIL: 'notification.auto_recharge_fail',
    AUTO_PAY_BATCH: 'notification.auto_pay_batch',
    // Billing dispatcher (migrated off hardcoded VN strings —
    // dbg-20260704-110206-3372)
    INVOICE_SENT: 'notification.invoice_sent',
    INVOICE_PAID: 'notification.invoice_paid',
    INVOICE_PAYMENT_SUBMITTED: 'notification.invoice_payment_submitted',
    INVOICE_PAYMENT_REJECTED: 'notification.invoice_payment_rejected',
    CREDIT_MEMO_ISSUED: 'notification.credit_memo_issued',
    INVOICE_OVERDUE: 'notification.invoice_overdue',
    INVOICE_OVERDUE_FINAL: 'notification.invoice_overdue_final',
    // Credit lifecycle workers
    CREDIT_EXPIRY_7D: 'notification.credit_expiry_7d',
    CREDIT_EXPIRY_1D: 'notification.credit_expiry_1d',
    CREDIT_REMIND_AT_AMOUNT: 'notification.credit_remind_at_amount',
    CREDIT_SWEEP_APPLIED: 'notification.credit_sweep_applied',
    // CRM
    CRM_ATTACHMENT_INFECTED: 'notification.crm_attachment_infected',
    // Order lifecycle (merchant + vendor feeds; also used by seed fixtures)
    ORDER_ASSIGNED: 'notification.order_assigned',
    // Batch D (vendor-audit P1-12): admin alert when auto-routing lands an item
    // in pending_reassignment — the outbox row previously had no consumer.
    VENDOR_REASSIGNMENT_NEEDED: 'notification.vendor_reassignment_needed',
    ORDER_PAID: 'notification.order_paid',
    ORDER_SHIPPED: 'notification.order_shipped',
    ORDER_DELIVERED: 'notification.order_delivered',
    ORDER_CANCELLED: 'notification.order_cancelled',
    ORDER_QC_FAILED: 'notification.order_qc_failed',
    ORDER_QC_PASSED: 'notification.order_qc_passed',
    ORDER_UNPAID_AUTOCANCELLED: 'notification.order_unpaid_autocancelled',
    // Tracking / Return-to-Sender (Phase 4.11 — P4.11.D-08 / WF-001). RTS is the
    // ONLY tracking event that notifies; delivered/stale/exception stay passive
    // (no notify — anti-spam). Fanned out to admin (→ RTS queue) + merchant
    // (→ order detail) with i18n_key + link_url by notifyRtsDetected
    // (apps/api/src/modules/orders/jobs/rts-notify.ts).
    TRACKING_RTS_DETECTED: 'notification.tracking_rts_detected',
    // Phase 4.10 (Task 12) — merchant order-approval / send-to-production feed
    // (P4.10.D-04). Emitted on 3 events: (a) order halts at `waiting`
    // (Waiting Approval) on the confirm path, (b) the delayed auto-push fired
    // (system actor), (c) an admin sent the merchant's order on-behalf. Each is
    // dispatched with i18n_key + i18n_params({{orderNumber}}) + link_url
    // (/orders/:id) — never a literal (dbg-20260704-110206-3372). 3 locales.
    // vg-binding: CONTEXT:decisions=P4.10.D-04,P4.10.D-23
    ORDER_WAITING: 'notification.order_waiting',
    ORDER_AUTO_PUSHED: 'notification.order_auto_pushed',
    ORDER_ADMIN_SENT: 'notification.order_admin_sent',
    // Phase 4.12 (Task 12) — confirm-time charge notification (P4.12.D-33).
    // Params: {orderNumber, amount, amountCents}. `amountCents` is the integer
    // cents copied VERBATIM from `order.costSnapshot.totalCents` (tax + shipping
    // included, TS-32); `amount` is its display projection. Producer:
    // `notifyOrderConfirmedCharged` (orders/notifications/order-approval-notify).
    ORDER_CONFIRMED_CHARGED: 'notification.order_confirmed_charged',
    // Merchant order approval (Phase 4.10) — order stuck in `waiting`
    // ("Waiting Approval") past the per-store SLA threshold; the
    // order-waiting-sla worker (P4.10.D-12) nudges the merchant owner.
    ORDER_WAITING_SLA_REMINDER: 'notification.order_waiting_sla_reminder',
    // Product lifecycle
    PRODUCT_PUBLISHED: 'notification.product_published',
    PRODUCT_APPROVED: 'notification.product_approved',
    PRODUCT_REJECTED: 'notification.product_rejected',
    PRODUCT_SYNC_FAILED: 'notification.product_sync_failed',
    PRODUCT_ARCHIVED: 'notification.product_archived',
    // Store lifecycle
    STORE_CONNECTED: 'notification.store_connected',
    STORE_DISCONNECTED: 'notification.store_disconnected',
    STORE_SYNC_STARTED: 'notification.store_sync_started',
    STORE_SYNC_COMPLETED: 'notification.store_sync_completed',
    // Wallet extras
    WALLET_CHARGED: 'notification.wallet_charged',
    WALLET_PAYOUT_SENT: 'notification.wallet_payout_sent',
    PAYOUT_AVAILABLE: 'notification.payout_available',
    AUTO_PAY_RETRY_EXHAUSTED: 'notification.auto_pay_retry_exhausted',
    AUTO_RECHARGE_DISABLED: 'notification.auto_recharge_disabled',
    // Billing extras
    INVOICE_DUE: 'notification.invoice_due',
    REFUND_ISSUED: 'notification.refund_issued',
    // System
    SYSTEM_MAINTENANCE: 'notification.system_maintenance',
    SYSTEM_RELEASE: 'notification.system_release',
    SECURITY_ALERT: 'notification.security_alert',
    KYC_UPDATE: 'notification.kyc_update',
    // Support agent assignment (P9.3.D-09 — in-app + Telegram + Lark outbound)
    SUPPORT_TICKET_ASSIGNED: 'notification.support_ticket_assigned',
    SUPPORT_TICKET_FORCE_REASSIGNED: 'notification.support_ticket_force_reassigned',
    // unified-ticket §7.1 (Plan 5) — merchant owner notified when Support opens
    // a ticket ON THEIR BEHALF (origin='admin').
    SUPPORT_TICKET_OPENED_BY_ADMIN: 'notification.support_ticket_opened_by_admin',
    // unified-ticket §4.1.1 (Plan 5) — assignee notified when the merchant owner
    // reopens / confirms-close a Resolved ticket (self-service).
    SUPPORT_TICKET_REOPENED_BY_MERCHANT: 'support.ticket.reopened_by_merchant',
    SUPPORT_TICKET_CLOSED_BY_MERCHANT: 'support.ticket.closed_by_merchant',
    // unified-ticket §7.1 — merchant owner notified when their ticket → Resolved
    // (in-app + Lark/Telegram mirror; email is a separate transactional path).
    SUPPORT_TICKET_RESOLVED: 'notification.support_ticket_resolved',
    // Support staff reply → merchant owner in-app notif (dbg-20260706 support
    // tab alert; feeds merchant-v3 tab badge + chime). Body preview + subject
    // are user data passed as i18n_params, not literals. Two variants: with a
    // ticket subject in the title, and a subject-less fallback.
    SUPPORT_TICKET_REPLY: 'notification.support_ticket_reply',
    SUPPORT_TICKET_REPLY_NO_SUBJECT: 'notification.support_ticket_reply_no_subject',
    // Inbound direction (issue #2555 / #2675) — the support desk had NO
    // notification producer for the two events the desk actually waits on: a
    // merchant opening a ticket, and a merchant replying to one. Only
    // assignment/dispatch produced staff notifications, so the topbar bell stayed
    // empty while the sidebar badge (a separate ticket-count query) showed work.
    // Recipients are the support roster (roleType admin/superadmin/support), or
    // just the assignee once a ticket is owned.
    SUPPORT_TICKET_CREATED_FOR_DESK: 'notification.support_ticket_created_for_desk',
    SUPPORT_TICKET_MERCHANT_REPLY: 'notification.support_ticket_merchant_reply',
    // Merchant rated a ticket (dbg-20260706 feedback (a)) — assigned agent gets
    // every rating; ratings ≤2★ additionally escalate to the Super Admin pool.
    SUPPORT_TICKET_RATED: 'notification.support_ticket_rated',
    SUPPORT_TICKET_RATED_LOW: 'notification.support_ticket_rated_low',
    // Ticket resolved → invite the merchant to rate the support they received
    // (feedback (c), dbg-20260707).
    SUPPORT_TICKET_RESOLVED_RATE: 'notification.support_ticket_resolved_rate',
    // Support social channel health (P9.4.D-15 — channel-down + Zalo token
    // refresh alerts; Super Admin ops surface, no literal display strings).
    SUPPORT_CHANNEL_DOWN: 'support.channel.down',
    SUPPORT_ZALO_REFRESH_FAILED: 'support.channel.zalo_refresh_failed',
    // Support Bridge (Plan D) — a social bridge device (e.g. a Facebook
    // personal-account bridge) dropped offline / logged out and can no longer
    // relay messages; Super Admin ops surface. Params: {platform, name, reason}.
    SUPPORT_BRIDGE_OFFLINE: 'support.bridge.offline',
    // unified-ticket §7.1/§7.3 — dispatch fan-out (ticket-dispatch.handler.ts).
    // Previously emitted with hardcoded VI title/body and NO catalog entry, so
    // the in-app feed could not re-render at the recipient's locale (C1).
    SUPPORT_DISPATCH_CREATED: 'support.dispatch.created',
    SUPPORT_DISPATCH_HANDLER_REPLY: 'support.dispatch.handler_reply',
    SUPPORT_DISPATCH_RETURNED: 'support.dispatch.returned',
    SUPPORT_DISPATCH_VENDOR_REPLY: 'support.dispatch.vendor_reply',
    // Vendor allocation proposals (P4.4.2.D-16 — dual-approval fanout).
    // created → every admin with `vendor_allocations:manage` EXCEPT proposer
    // (self-suppression, G-24-e1); approved/rejected → proposer only (G-24-e2).
    // Every notif carries link_url=/vendors/allocations?tab=proposals.
    // Params: created {proposer, reason}; approved {approver}; rejected {approver, reason}.
    VENDOR_ALLOCATION_PROPOSAL_CREATED: 'vendor_allocation.proposal.created',
    VENDOR_ALLOCATION_PROPOSAL_APPROVED: 'vendor_allocation.proposal.approved',
    VENDOR_ALLOCATION_PROPOSAL_REJECTED: 'vendor_allocation.proposal.rejected',
    // Merchant team/members (Phase 6.1 P6.1.D-20/D-28) — a member accepted an
    // invite but the team they were invited into was deactivated before they
    // accepted, so they landed no-team; the shop owner is notified to re-assign.
    MEMBER_LANDED_NO_TEAM: 'notification.member_landed_no_team',
    // Phase 14.1 embroidery 2-step pricing (P14.1.D-04 / P14.1.D-18) — merchant
    // + vendor money-adjacent events. Rendered by User.language; the persist
    // worker re-renders on locale switch. Money values are passed pre-formatted
    // as i18n_params (formatUsdCents server-side), never embedded as literals.
    // (1) emb fee ready to accept + 72h deadline (merchant); (2) order
    // auto-cancel + wallet refund on deadline expiry; (3) admin pricingProfile
    // tag change (informational); (4) admin adjustment surcharge debited.
    EMB_FEE_AWAITING_ACCEPT: 'notification.emb_fee_awaiting_accept',
    EMB_ORDER_AUTOCANCELLED_REFUND: 'notification.emb_order_autocancelled_refund',
    PRICING_PROFILE_CHANGED: 'notification.pricing_profile_changed',
    ADMIN_SURCHARGE_DEBITED: 'notification.admin_surcharge_debited',
    // Test/UAT fixture rows (seeded sample notifications)
    UAT_SAMPLE: 'notification.uat_sample',
    // -----------------------------------------------------------------------
    // Phase 15 (Notification & Email Consolidation) Task 11 — taxonomy merge
    // (P15.D-13 / TS-01 / TS-21). The P4.6 `template-registry.ts` used to be a
    // SECOND taxonomy source (its own subjectI18n/bodyI18n catalog keys). Per
    // D-13 the ONE canonical taxonomy is this registry: every legacy P4.6
    // notify()-lane event type now resolves to a canonical key via
    // `LEGACY_EVENT_TO_CANONICAL_KEY` (template-registry.ts). The keys below are
    // the ones with no pre-existing canonical equivalent — added here so the
    // merged taxonomy renders in all 3 locales (assertCatalogComplete guard).
    // Legacy events that DID have a canonical equivalent map to the existing key
    // (e.g. order.charged→ORDER_PAID, order.assigned→ORDER_ASSIGNED) and are NOT
    // duplicated here.
    // vg-binding: CONTEXT:decisions=P15.D-13,P15.D-11,P15.D-02
    ORDER_CREATED: 'notification.order_created',
    ORDER_CHARGE_FAILED: 'notification.order_charge_failed',
    ORDER_IN_PRODUCTION: 'notification.order_in_production',
    REFUND_FAILED: 'notification.refund_failed',
    DISPUTE_OPENED: 'notification.dispute_opened',
    DISPUTE_EVIDENCE_REQUIRED: 'notification.dispute_evidence_required',
    DISPUTE_RESOLVED_REFUND: 'notification.dispute_resolved_refund',
    DISPUTE_RESOLVED_RESEND: 'notification.dispute_resolved_resend',
    DISPUTE_RESOLVED_REJECTED: 'notification.dispute_resolved_rejected',
    LABEL_PURCHASED: 'notification.label_purchased',
    LABEL_VOID_FAILED: 'notification.label_void_failed',
    LABEL_REBUY_REQUIRED: 'notification.label_rebuy_required',
    LABEL_TRACKING_STALLED: 'notification.label_tracking_stalled',
    EMBROIDERY_STEP2_REMINDER_24H: 'notification.embroidery_step2_reminder_24h',
    ADDRESS_CHANGE_REQUESTED: 'notification.address_change_requested',
    ADDRESS_CHANGE_CONFIRMED: 'notification.address_change_confirmed',
    ADDRESS_CHANGE_EXPIRED: 'notification.address_change_expired',
    VENDOR_RATING_DROPPED: 'notification.vendor_rating_dropped',
    VENDOR_ASSIGNMENT_REVOKED: 'notification.vendor_assignment_revoked',
    VENDOR_QUALITY_WARNING: 'notification.vendor_quality_warning',
    WEBHOOK_DELIVERY_EXHAUSTED_FINANCIAL: 'notification.webhook_delivery_exhausted_financial',
    WEBHOOK_SUBSCRIPTION_DISABLED: 'notification.webhook_subscription_disabled',
    // Live producer: apps/api/src/workers/artwork-ingest/process-area.service.ts
    // (customer-fault artwork validation error → merchant feed). This is the only
    // "truly new" key with a live notify()-lane producer.
    ARTWORK_INGEST_ERROR: 'notification.artwork_ingest_error',
    // -----------------------------------------------------------------------
    // Phase 15 (Notification & Email Consolidation) Task 12 — publishCrmEvent
    // wrapper migration (P15.D-13 / P15.D-11 / TS-01). The P10
    // `crm-notification.publisher.publishCrmEvent()` used to email affiliate
    // payout transitions inline with a hardcoded VI subject + NO i18n_key, so the
    // feed could never re-render at the recipient's locale. Its one live producer
    // (affiliate/routes/payout.routes.ts) now delegates in-app + email to the
    // canonical `fanout()` path through this key. Copy is param-light (only the
    // always-present `{{payout_id}}`) so it renders cleanly across the
    // requested/approved/rejected/paid transitions the producer emits under the
    // single `affiliate.payout.completed` event. 3 locales (assertCatalogComplete
    // guard).
    // vg-binding: CONTEXT:decisions=P15.D-13,P15.D-11
    AFFILIATE_PAYOUT_COMPLETED: 'notification.affiliate_payout_completed',
    // -----------------------------------------------------------------------
    // UAT sweep P1-5 — vendor suspend/reactivate previously produced ZERO
    // comms (no bell, no email): the vendor only discovered the suspension via
    // a silent 403 banner. Producers: admin/handlers/vendor-suspend.ts +
    // vendor-reactivate.ts (personal `user` audience = the vendor owner userId).
    // Params: suspended {reason}; reactivated (none).
    ACCOUNT_SUSPENDED: 'notification.account_suspended',
    ACCOUNT_REACTIVATED: 'notification.account_reactivated',
    // UAT sweep P1-7 — catalog/billing workers used to emit fanouts with no
    // audience (or a local console stub), so nothing ever reached a feed.
    // blueprint_updated → per affected merchant; deal_expiry_warning → per
    // merchant resolved from Deal.scope (the old recipient was the dealId —
    // never a user); embroidery_stitch_count_failed → admin scope;
    // invoice_generation_failed → the affected merchant.
    BLUEPRINT_UPDATED: 'notification.blueprint_updated',
    DEAL_EXPIRY_WARNING: 'notification.deal_expiry_warning',
    EMBROIDERY_STITCH_COUNT_FAILED: 'notification.embroidery_stitch_count_failed',
    INVOICE_GENERATION_FAILED: 'notification.invoice_generation_failed',
    // Phase 17 Task 47 (P17.D-44) — a merchant routing rule was the layer that
    // emptied the vendor pool, so the item pends. P17.D-04 removed dual approval
    // from those rules (one admin writes them, they take effect immediately);
    // this notification is the counterweight that purchase paid for, and it goes
    // to the admins holding `orders:manage_assignment_rules` — the same
    // permission that gates writing the rule, so the warning always lands on
    // someone who can undo it.
    //
    // Deliberately NOT sent to the vendor and NOT sent to any merchant user
    // (P17.D-43): telling the vendor would leak which merchant blocked it — a
    // merchant the vendor cannot otherwise see (P17.D-23).
    //
    // Params: {merchantName, variantLabel, blueprintName, orderCount}. Producer:
    // `vendor/services/routing-pool-empty-notification.service.ts`. Nothing is
    // pre-rendered there; the read layer renders per recipient `User.language`.
    // vg-binding: CONTEXT:decisions=P17.D-04,P17.D-31,P17.D-43,P17.D-44
    ROUTING_POOL_EMPTY_MERCHANT_RULE: 'notification.routing_pool_empty_merchant_rule',
    // -----------------------------------------------------------------------
    // Phase 18 Task 06 (P18.D-36) — the vendor Integration API lane. P18.D-04
    // and P18.D-13 both say "notify admin + vendor", but no catalog key existed
    // for any of those four events, and tests run `I18N_NOTIF_GUARD=strict`
    // (apps/api/src/tests/setup.ts) where a fanout without an `i18n_key` THROWS
    // rather than warns — so the keys have to land before their producers, not
    // with them.
    //
    // Params are deliberately param-light: every placeholder below is a value
    // the producer ALWAYS has at fanout time. `interpolate()` leaves an unfilled
    // `{{placeholder}}` verbatim in the rendered string, so an optional param
    // would ship raw mustaches to a real feed. Free-text reasons are therefore
    // NOT interpolated here — they belong in the delivery log / audit row.
    // Producers (task-28 dispatcher, task-30 admin routes, task-31 key
    // lifecycle) MUST pass exactly these params:
    //   VENDOR_WEBHOOK_DELIVERY_FAILED       {vendorName, endpoint, eventType, attempts}
    //   VENDOR_WEBHOOK_AUTO_DISABLED         {vendorName, endpoint, failureCount}
    //   VENDOR_INTEGRATION_KEY_REVOKED       {vendorName, keyPrefix}
    //   VENDOR_INTEGRATION_DISABLED_BY_ADMIN {vendorName, actor}
    //
    // Copy encodes the two invariants the recipient has to act on: the failed
    // delivery does NOT move the assignment off the vendor (P18.D-04 — no new
    // state, no silent re-route), and recovery is PULL-only via GET /assignments
    // + GET /events — individual webhook events are never replayed (P18.D-13).
    //
    // No email template rows are added to
    // `apps/api/src/modules/notifications/template-registry.ts`: since the P15
    // taxonomy merge that file is a compatibility shim whose own header forbids
    // new copy ("do NOT add new copy here — add a NOTIFICATION_KEYS entry in
    // packages/i18n"), and the email lane renders from this canonical catalog.
    //
    // vg-binding: P18.D-04
    // vg-binding: P18.D-13
    // vg-binding: P18.D-36
    // vg-binding: CONTEXT:decisions=P18.D-04,P18.D-13,P18.D-36
    VENDOR_WEBHOOK_DELIVERY_FAILED: 'notification.vendor_webhook_delivery_failed',
    VENDOR_WEBHOOK_AUTO_DISABLED: 'notification.vendor_webhook_auto_disabled',
    VENDOR_INTEGRATION_KEY_REVOKED: 'notification.vendor_integration_key_revoked',
    VENDOR_INTEGRATION_DISABLED_BY_ADMIN: 'notification.vendor_integration_disabled_by_admin',
    // -----------------------------------------------------------------------
    // Phase 18 Task 36 (P18.D-13 / P18.D-35) — the FLEET alert.
    //
    // The four keys above are per-vendor: each one names an endpoint or a key
    // and lands in that vendor's feed. None of them can say the sentence an
    // operator needs when the break is OURS (expired cert, DNS change, a worker
    // booted without its KEK), because in that case every vendor fails
    // identically and crosses the 20-event auto-disable ceiling at its own pace
    // — producing a trickle of N late per-vendor notifications and no single
    // signal. This key is that single signal, addressed to the ADMIN audience
    // (one global row, not one per admin) and rate-limited to at most one per
    // cooldown window by the producer.
    //
    // Producer: `vendor-integration/handlers/admin/aggregate.get.ts`
    //   VENDOR_INTEGRATION_FLEET_ALERT {failedEvents, vendorCount, autoDisabled, windowHours}
    // All four are counts the producer always holds at fanout time (the
    // param-light rule above): `interpolate()` leaves an unfilled placeholder
    // verbatim, so an optional param would ship raw mustaches into a real feed.
    //
    // vg-binding: P18.D-13
    // vg-binding: P18.D-35
    VENDOR_INTEGRATION_FLEET_ALERT: 'notification.vendor_integration_fleet_alert',
};
/**
 * Runtime guard — used by dispatcher to reject unknown event_type values.
 */
export function isNotificationKey(value) {
    return Object.values(NOTIFICATION_KEYS).includes(value);
}
/**
 * Flat array form for Zod enum / iteration.
 */
export const NOTIFICATION_KEY_LIST = Object.values(NOTIFICATION_KEYS);
export const SUPPORTED_LOCALES = [
    'en',
    'vi',
    'fr',
];
export const FALLBACK_LOCALE = 'en';
//# sourceMappingURL=notifications-keys.js.map